/* eslint-disable max-lines */
import { reject } from 'ramda';
import * as types from '../mutation-types';
import { apolloClient } from '@/apollo';
import { computeCodeOccurrences } from '@/apollo/cache';
import updateProjectCache from '@utils/updateProjectCache';
import { updateSourceInCache, updateGroupContentsInCache } from '@utils/updateEntitiesInCache';
import {
  ProjectDocument,
  ProjectQuery,
  ProjectQueryVariables,
  CreateSourceDocument,
  DeleteSourceDocument,
  RestoreSourceDocument,
  UpdateSourceDocument,
  AddSourceToGroupDocument,
  RemoveSourceFromGroupDocument,
  DuplicateSourceDocument,
  DuplicateSourceWithCodesDocument,
  GroupDefaultFragmentDoc,
} from '@/graphql/operations.gen';

// initial state
const initialState = {};

// getters
const getters = {};

// actions
const actions = {
  /**
   * Create source.
   *
   * @param {*} context
   * @param {*} payload
   */
  create({ commit }, { projectId, name, content, label, file }) {
    commit(types.SOURCE_CREATE, {
      meta: {
        analytics: [['event', 'source', 'create', label]],
      },
    });

    return apolloClient
      .mutate({
        mutation: CreateSourceDocument,
        variables: {
          projectId,
          name,
          content,
          file,
        },
        update(store, response) {
          updateProjectCache(store, projectId, (project) => ({
            sources: [...project.sources, response.data.createSource],
          }));

          return response;
        },
      })
      .then((data) => data.data.createSource);
  },

  /**
   * Delete source.
   *
   * @param {*} context
   * @param {*} payload
   */
  delete({ commit }, { id, projectId }) {
    commit(types.SOURCE_DELETE, {
      meta: {
        analytics: [['event', 'source', 'delete']],
      },
    });

    return apolloClient
      .mutate({
        mutation: DeleteSourceDocument,
        variables: {
          id,
        },
        update(cache, response) {
          updateProjectCache(cache, projectId, (cachedProject) => ({
            sources: reject((source) => source.id === id, cachedProject.sources),
          }));

          const { project } = cache.readQuery<ProjectQuery, ProjectQueryVariables>({
            query: ProjectDocument,
            variables: {
              id: projectId,
            },
          });

          computeCodeOccurrences(project);

          return response;
        },
      })
      .then((data) => data.data.deleteSource);
  },

  /**
   * Restoure source.
   *
   * @param {*} context
   * @param {*} payload
   */
  restore({}, { id, projectId }) {
    return apolloClient.mutate({
      mutation: RestoreSourceDocument,
      variables: {
        id,
      },
      update(cache, response) {
        updateProjectCache(cache, projectId, (project) => ({
          sources: [...project.sources, response.data.restoreSource],
        }));

        const { project } = cache.readQuery<ProjectQuery, ProjectQueryVariables>({
          query: ProjectDocument,
          variables: {
            id: projectId,
          },
        });

        computeCodeOccurrences(project);

        return response;
      },
    });
  },

  /**
   * Update source.
   *
   * @param {*} context
   * @param {*} payload
   */
  update({}, data) {
    return apolloClient.mutate({
      mutation: UpdateSourceDocument,
      variables: {
        ...data,
      },
      optimisticResponse: {
        __typename: 'Mutation',
        updateSource: {
          ...data,
          __typename: 'Source',
        },
      },
    });
  },

  addToGroup(currentState, { id, group }) {
    const cachedGroup = apolloClient.readFragment({
      id: `Group:${group.id}`,
      fragment: GroupDefaultFragmentDoc,
      fragmentName: 'GroupDefault',
    });

    return apolloClient.mutate({
      mutation: AddSourceToGroupDocument,
      variables: {
        id,
        groupId: group.id,
      },
      optimisticResponse: {
        __typename: 'Mutation',
        addToGroup: cachedGroup,
      },
      update(store, response) {
        const groupData = response.data.addToGroup;

        const source = updateSourceInCache(store, { id }, (cachedSource) => {
          if (!cachedSource.groups.find((sGroup) => sGroup.id === group.id)) {
            return {
              groups: [...cachedSource.groups, { ...cachedGroup, ...groupData }],
            };
          }

          return {
            groups: cachedSource.groups,
          };
        });

        updateGroupContentsInCache(store, group.id, (data) => ({
          contents: [...data.contents, source],
        }));

        return groupData;
      },
    });
  },

  removeFromGroup(currentState, { id, group }) {
    return apolloClient.mutate({
      mutation: RemoveSourceFromGroupDocument,
      variables: {
        id,
        groupId: group.id,
      },
      optimisticResponse: {
        __typename: 'Mutation',
        removeFromGroup: {
          id: group.id,
          name: group.name,
          __typename: 'Group',
        },
      },
      update(cache, response) {
        const responseData = response.data.removeFromGroup;

        updateSourceInCache(cache, { id }, (source) => ({
          groups: reject((sGroup) => sGroup.id === responseData.id, source.groups),
        }));

        updateGroupContentsInCache(cache, group.id, (data) => ({
          contents: data.contents.filter((source) => source.id !== id),
        }));

        return responseData;
      },
    });
  },

  /**
   * Duplicate source.
   *
   * @param {*} context
   * @param {*} payload
   */
  duplicate({ commit }, { id, projectId, keepCodes }) {
    let mutation = DuplicateSourceDocument;
    const analyticsTags = ['event', 'source', 'duplicate'];

    if (keepCodes) {
      mutation = DuplicateSourceWithCodesDocument;
      analyticsTags.push('withCodes');
    }

    commit(types.SOURCE_DUPLICATE, {
      meta: {
        analytics: [analyticsTags],
      },
    });

    return apolloClient
      .mutate({
        mutation,
        variables: {
          id,
        },
        update(store, response) {
          updateProjectCache(store, projectId, (project) => ({
            sources: [...project.sources, response.data.duplicateSource],
          }));

          return response;
        },
      })
      .then((data) => data.data.duplicateSource);
  },
};

// mutations
const mutations = {
  [types.SOURCE_CREATE]() {},

  [types.SOURCE_UPDATE]() {},

  [types.SOURCE_DELETE]() {},

  [types.SOURCE_DUPLICATE]() {},
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
